import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Select from 'flarum/common/components/Select';
import Icon from 'flarum/common/components/Icon';
import Stream from 'flarum/common/utils/Stream';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import { field, section } from './Field';
import * as ClubRules from '../utils/clubRules';
import { teamSection } from './TeamSection';
import { miniSection } from './MiniTeamSection';
import { bannerSection } from './BannerSection';
const t = (key, params) => app.translator.trans('ppk2-profile.forum.' + key, params);
export const NONE = 'none';
export const NEW = 'new';

/**
 * Club part of the modal state. `initial` is the ppk2Club attribute:
 * { id, name, forumUrl, clubStatus, role, status } or null.
 */
export function clubState(initial) {
  return {
    initial: initial || null,
    clubs: null,
    // loaded from GET /api/ppk2/clubs
    loadError: false,
    showErrors: false,
    // set by Save: show «name required» on an untouched field
    choice: Stream(initial ? 'c' + initial.id : NONE),
    newName: Stream(''),
    // club address, e.g. pp2forum.ru
    role: Stream(initial ? initial.role : ClubRules.DEFAULT_ROLE)
  };
}
export function loadClubs(cs) {
  return app.request({
    method: 'GET',
    url: app.forum.attribute('apiUrl') + '/ppk2/clubs'
  }).then(response => {
    cs.clubs = response.clubs || [];
    // The current club must always be selectable, even if the list changed meanwhile.
    if (cs.initial && !cs.clubs.some(c => c.id === cs.initial.id)) {
      cs.clubs.push({
        id: cs.initial.id,
        name: cs.initial.name,
        status: cs.initial.clubStatus
      });
    }
  }).catch(() => {
    cs.loadError = true;
  }).finally(() => m.redraw());
}

/** Local (format) problem of the new club address: [key, params] or null. */
export function clubLocalError(cs) {
  if (cs.choice() !== NEW) return null;
  return ClubRules.check(ClubRules.normalize(cs.newName()));
}

/** Value for PATCH (ppk2Club) or `undefined` when nothing changed (not sent at all). */
export function clubPayload(cs) {
  const choice = cs.choice();
  const init = cs.initial;
  if (choice === NONE) return init ? null : undefined;
  if (choice === NEW) {
    return {
      newClub: {
        name: ClubRules.normalize(cs.newName())
      },
      role: cs.role()
    };
  }
  const id = parseInt(choice.substr(1), 10);
  if (init && init.id === id && init.role === cs.role()) return undefined;
  return {
    clubId: id,
    role: cs.role()
  };
}

/**
 * Tab «Клуб и команда»: «КЛУБ», «КОМАНДЫ» (main team + mini-teams), «БАННЕР КОМАНДЫ».
 * Attrs: state (modal state with .club, .team, .mini), user, loading.
 */
export default class ClubTab extends Component {
  selectedClub() {
    const cs = this.attrs.state.club;
    const choice = cs.choice();
    if (!cs.clubs || choice === NONE || choice === NEW) return null;
    const id = parseInt(choice.substr(1), 10);
    return cs.clubs.find(c => c.id === id) || null;
  }

  /** Status shown next to the role: saved status if unchanged, otherwise «Не проверено». */
  roleStatus() {
    const cs = this.attrs.state.club;
    return clubPayload(cs) === undefined && cs.initial ? cs.initial.status : 'unverified';
  }
  error(err) {
    return err ? m("div", {
      className: "Ppk2NickStatus Ppk2NickStatus--invalid"
    }, m(Icon, {
      name: "fas fa-circle-xmark"
    }), " ", app.translator.trans(err[0], err[1])) : null;
  }
  clubBody() {
    const cs = this.attrs.state.club;
    const loading = this.attrs.loading;
    if (cs.loadError) return m("div", {
      className: "Ppk2NickStatus Ppk2NickStatus--invalid"
    }, t('club_team.club.load_error'));
    if (!cs.clubs) return m(LoadingIndicator, {
      display: "inline",
      size: "small"
    });
    const options = {
      [NONE]: t('club_team.club.none')
    };
    cs.clubs.forEach(c => options['c' + c.id] = c.name);
    options[NEW] = t('club_team.club.add_new');
    const error = clubLocalError(cs);
    const showError = error && (cs.newName() !== '' || cs.showErrors);
    const normalized = ClubRules.normalize(cs.newName());
    return [m(Select, {
      className: "Ppk2ClubSelect",
      name: "ppk2Club",
      options: options,
      value: cs.choice(),
      disabled: loading,
      onchange: value => cs.choice(value)
    }), cs.choice() === NEW ? m("div", {
      className: "Ppk2NewClub"
    }, m("input", {
      className: 'FormControl' + (showError ? ' Ppk2NickInput--invalid' : ''),
      name: "ppk2NewClub",
      type: "text",
      inputmode: "url",
      maxlength: ClubRules.MAX_LENGTH + 20,
      autocomplete: "off",
      spellcheck: "false",
      placeholder: t('club_team.club.new_placeholder'),
      value: cs.newName(),
      oninput: e => cs.newName(e.target.value),
      disabled: loading
    }), showError ? this.error(error) : null, !error && normalized !== cs.newName() ? m("div", {
      className: "Ppk2Note"
    }, m(Icon, {
      name: "fas fa-check"
    }), " ", t('club_team.club.will_be_saved_as', {
      name: normalized
    })) : null, m("div", {
      className: "Ppk2Note"
    }, m(Icon, {
      name: "fas fa-circle-info"
    }), " ", t('club_team.club.new_note'))) : null];
  }
  statusBadge(status) {
    const verified = status === 'verified';
    return m("span", {
      className: 'Ppk2StatusBadge Ppk2StatusBadge--' + (verified ? 'verified' : 'unverified'),
      title: t(verified ? 'status.verified' : 'status.not_verified')
    }, m(Icon, {
      name: verified ? 'fas fa-circle-check' : 'fas fa-clock'
    }), " ", m("span", {
      className: "Ppk2StatusBadge-text"
    }, t(verified ? 'status.verified' : 'status.not_verified')));
  }
  view() {
    const cs = this.attrs.state.club;
    const club = this.selectedClub();
    const roleOptions = {};
    ClubRules.ROLES.forEach(r => roleOptions[r] = t('club_team.role.roles.' + r));
    return m("div", {
      className: "Ppk2Tab Ppk2Tab--clubTeam"
    }, section(t('club_team.section_club')), field('club', t('club_team.club.label'), this.clubBody(), club ? m("a", {
      className: "Ppk2ClubForumLink",
      href: ClubRules.forumUrl(club.name),
      target: "_blank",
      rel: "noopener noreferrer nofollow",
      title: t('club_team.club.forum_link')
    }, m(Icon, {
      name: "fas fa-arrow-up-right-from-square"
    }), " ", m("span", {
      className: "Ppk2ClubForumLink-text"
    }, t('club_team.club.forum_link'))) : null, m("span", {
      className: "Ppk2Tip-multiline"
    }, t('club_team.club.tip', {
      max: ClubRules.MAX_LENGTH
    }))), cs.clubs && cs.choice() !== NONE ? field('clubRole', t('club_team.role.label'), m(Select, {
      className: "Ppk2RoleSelect",
      name: "ppk2ClubRole",
      options: roleOptions,
      value: cs.role(),
      disabled: this.attrs.loading,
      onchange: value => cs.role(value)
    }), this.statusBadge(this.roleStatus()), m("span", {
      className: "Ppk2Tip-multiline"
    }, t('club_team.role.tip'))) : null, teamSection(this.attrs.state.team, this.attrs.user, this.attrs.loading), miniSection(this.attrs.state.mini, this.attrs.user, this.attrs.loading), bannerSection(this.attrs.state.team, this.attrs.user, this.attrs.loading));
  }
}
flarum.reg.add('ppk2-profile', 'forum/components/ClubTab', ClubTab);